import React, { useEffect, useMemo } from 'react';
import { Card, Spin, Checkbox, Button, Typography, Input, Divider } from 'antd';
import type { RoleCreate, RoleUpdate } from '../types/role.types';
import { useTranslation } from 'react-i18next';
import { DefaultValues, Path } from 'react-hook-form';
import { ValidationError } from '../../../../../types/api-response.types';
import { AccessRoleBuilder } from '@gluai-studio/passport';
import { useForm, Controller } from 'react-hook-form';
import { PERMISSION_MATRIX, rbac } from '../../../../../permissions/permissions';
import { cloneDeepWith } from 'lodash';
import { SaveOutlined } from '@ant-design/icons';

const { Title } = Typography;

interface RoleFormProps {
  defaultValues?: DefaultValues<RoleCreate | RoleUpdate>;
  onFormSubmit: (data: RoleCreate | RoleUpdate, resetForm: () => void) => void;
  isLoading: boolean;
  validationErrors?: ValidationError[];
}

function RoleFormComponent({
  defaultValues,
  onFormSubmit,
  isLoading = false,
  validationErrors = [],
}: RoleFormProps) {
  const { t } = useTranslation();
  const { fields: roleFields } = rbac.generateRoleFormConfig(PERMISSION_MATRIX);

  const groupedFields = useMemo(() => {
    return roleFields.reduce<Record<string, typeof roleFields>>((acc, field) => {
      if (!acc[field.resource]) {
        acc[field.resource] = [];
      }
      acc[field.resource].push(field);
      return acc;
    }, {});
  }, [roleFields]);

  const { handleSubmit, control, reset, setValue } = useForm({
    defaultValues,
  });

  useEffect(() => {
    if (defaultValues) {
      setValue('name', defaultValues.name);
      setValue('permissions', defaultValues.permissions);
    }
  }, [defaultValues, setValue]);

  const onSubmit = (data: RoleCreate | RoleUpdate) => {
    // replace undefined with false
    const parsedData = cloneDeepWith(data, (value: unknown) => {
      if (value === undefined) {
        return false;
      }
      // Return undefined to let cloneDeepWith recurse into nested structures
      return undefined;
    });
    onFormSubmit(parsedData, () => {
      reset();
    });
  };

  return (
    <Spin spinning={isLoading} tip={t('common.submittingForm')}>
      <Card className="shadow-lg">
        <form onSubmit={handleSubmit(onSubmit as any)}>
          <Controller
            key="name"
            name="name"
            control={control}
            rules={{
              required: t('roles.name.required'),
            }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Input
                  {...field}
                  placeholder={t('roles.name.placeholder')}
                  status={error ? 'error' : ''}
                />
                {error && (
                  <Typography.Text type="danger" className="mt-1">
                    {error.message}
                  </Typography.Text>
                )}
              </div>
            )}
          />
          {Object.entries(groupedFields).map(([resource, actions]) => (
            <div key={resource} className="my-4">
              <Title level={4} className="mb-2" style={{ textTransform: 'capitalize' }}>
                {t(`modules.${resource}`)}
              </Title>
              <div className="flex flex-wrap gap-2 mt-3">
                {actions.map((action, index) => (
                  <>
                    <Controller
                      key={action.name}
                      name={`permissions.${action.name}` as Path<RoleCreate | RoleUpdate>}
                      control={control}
                      render={({ field }) => (
                        <Checkbox {...field} checked={!!field.value}>
                          {t(`roles.permissions.${action.action}`)}
                        </Checkbox>
                      )}
                    />
                    {index !== actions.length - 1 && <Divider type="vertical" className="mx-5" />}
                  </>
                ))}
              </div>
            </div>
          ))}

          <Button
            type="primary"
            size="large"
            htmlType="submit"
            className="mt-4 float-end"
            loading={isLoading}
            icon={<SaveOutlined />}
          >
            {t('common.save')}
          </Button>
        </form>
      </Card>
    </Spin>
  );
}

// Custom comparison function for memoization
function areEqual(prevProps: RoleFormProps, nextProps: RoleFormProps): boolean {
  return (
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.onFormSubmit === nextProps.onFormSubmit &&
    JSON.stringify(prevProps.defaultValues) === JSON.stringify(nextProps.defaultValues) &&
    JSON.stringify(prevProps.validationErrors) === JSON.stringify(nextProps.validationErrors)
  );
}

export default React.memo(RoleFormComponent, areEqual);
